import { HoldingTableDetails } from '@ddt-web/blending';
import { IdentifierType } from '@ddt-web/common';
import { BaseHolding, IdentifiersMap } from '@ddt-web/domain';
import { SecurityIdentifierUtil } from '@ddt-web/generic-utils';
import { PortfolioIdUtil } from '@ddt-web/portfolio-data';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';

import { WweSecurity } from '../domain/objects/wwe-security.model';

/*******************************************
 * HERE BE DRAGONS
 * Under no circumstances should we change any of the logic here, as this could cause api calls as well as security mapping strategies to fail!
 * Consult the team if there's functionality missing !!!
 *******************************************/
export class PortfolioSecurityIdentifierMappingUtil {
	private static readonly ASSET_TYPES_TO_USE_ALADDIN_ID = ['Cash', 'Index', 'ASSET_TYPE_CASH', 'ASSET_TYPE_INDEX'];

	public static allSecuritiesHaveIdentifier(positions: Array<BaseHolding>, identifier: string): boolean {
		return identifier ? positions.every(position => position[identifier]) : false;
	}

	public static buildSecurityKey(position: BaseHolding, identifier?: string): string {
		if (identifier) {
			return position[identifier];
		} else {
			return SecurityIdentifierUtil.buildSecurityAggregateKey(position);
		}
	}

	public static buildIdentifiersMap(position: WweSecurity | BaseHolding): IdentifiersMap {
		let idMap: IdentifiersMap = {
			morningstarSecId: get(position, 'morningstarSecId') || get(position, 'identifiers.morningstarSecId'),
			globalId: get(position, 'globalId')
				|| get(position, 'identifiers.globalId')
				|| PortfolioSecurityIdentifierMappingUtil.translateIdentifier(position, IdentifierType.GLOBAL_ID)
				|| PortfolioSecurityIdentifierMappingUtil.identifyPartnerIdentifier(position, IdentifierType.GLOBAL_ID),
			aladdinId: get(position, 'aladdinId')
				|| get(position, 'identifiers.aladdinId')
				|| PortfolioSecurityIdentifierMappingUtil.translateIdentifier(position, IdentifierType.BCUSIP)
				|| PortfolioSecurityIdentifierMappingUtil.identifyPartnerIdentifier(position, IdentifierType.BCUSIP)
				// setup for security with PAS data - aladdinId does not exist on AWA security 
				|| get(position, 'assetId')
				|| get(position, 'identifiers.assetId'),
			cusip: get(position, 'cusip') || get(position, 'identifiers.cusip')
				|| PortfolioSecurityIdentifierMappingUtil.translateIdentifier(position, IdentifierType.CUSIP)
				|| PortfolioSecurityIdentifierMappingUtil.identifyPartnerIdentifier(position, IdentifierType.CUSIP),
			isin: get(position, 'isin')
		};

		if (get(position, 'identifierType') === 'MODEL_ID' || get(position, 'identifierType') === 'THIRD_PARTY_ID') {

			const id = position?.['aladdinPortfolioCode'] || position?.['identifier'];
			idMap = { ...idMap, modelId: PortfolioIdUtil.isThirdPartyModel(id) && position.tpmId ? position.tpmId : id };
		}

		idMap = this.setAladdinIdForCashAndIndex(position, idMap);

		return idMap;
	}

	// set aladdinId to cusip for cash and index holdings (WDS) only - cusip is the only identifier we can use for it
	// we must only set aladdinId to cusip for WDS cash securities (only cash has the same cusip value in WDS and WWE)
	// assetType must be used for checking for Cash securities
	// this will be removed once we are fully on WWE
	public static setAladdinIdForCashAndIndex(position: WweSecurity | BaseHolding | HoldingTableDetails, identifiersMap: IdentifiersMap): IdentifiersMap {
		const updatedIdentifiersMap = { ...identifiersMap };

		if (PortfolioSecurityIdentifierMappingUtil.ASSET_TYPES_TO_USE_ALADDIN_ID.includes(position.assetType) && isEmpty(updatedIdentifiersMap['aladdinId'])) {
			updatedIdentifiersMap.aladdinId = position.cusip || PortfolioSecurityIdentifierMappingUtil.translateIdentifier(position, IdentifierType.CUSIP);
		}

		return updatedIdentifiersMap;
	}

	public static translateIdentifier(position: any, identifierType: string): string {
		if (position.identifierType === identifierType && position.identifier) {
			return position.identifier;
		}

		return null;
	}

	private static identifyPartnerIdentifier(position: any, identifierType: string): string {
		const otherIdentifiers = position?.allData?.otherIdentifiers ?? [];
		const matchedIdentifier = otherIdentifiers.find(identifier => identifier.identifierType.toLowerCase() === identifierType.toLowerCase());
		return matchedIdentifier?.identifier || null;
	}
}
